import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermission, PAGES, PAGE_INFO } from '../contexts/PermissionContext';

/**
 * 受保護的路由元件
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - 子元件
 * @param {string} props.page - 頁面 ID (使用權限系統)
 * @param {string} props.redirectTo - 無權限時重定向路徑 (預設 /login)
 */
const ProtectedRoute = ({
  children,
  page = null,
  redirectTo = '/login'
}) => {
  const { isAuthenticated, userProfile, loading: authLoading, hasPermission } = useAuth();
  const { canAccessPage, loading: permLoading, hasWildcardAccess } = usePermission();
  const location = useLocation();

  const loading = authLoading || permLoading;

  // 顯示載入中
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  // 未登入，重定向到登入頁
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // 檢查帳號狀態
  if (userProfile?.status === 'suspended') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 text-red-600 text-3xl mb-4">
              !
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">帳號已停用</h2>
            <p className="text-gray-600 mb-6">
              您的帳號已被停用，請聯繫系統管理員。
            </p>
            <button
              onClick={() => window.location.href = '/login'}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
            >
              返回登入頁
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 頁面權限檢查
  if (page) {
    // 管理員 (萬用權限) 可存取所有頁面
    if (!hasWildcardAccess() && !canAccessPage(page)) {
      return <AccessDenied page={page} />;
    }
  }

  return children;
};

/**
 * 無權限存取頁面
 */
const AccessDenied = ({ page = null }) => {
  const pageInfo = page ? PAGE_INFO[page] : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-100 text-yellow-600 text-3xl mb-4">
            ⚠
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">權限不足</h2>
          {pageInfo ? (
            <p className="text-gray-600 mb-4">
              您沒有權限存取「{pageInfo.icon} {pageInfo.label}」頁面。
            </p>
          ) : (
            <p className="text-gray-600 mb-4">
              您沒有權限存取此頁面。
            </p>
          )}
          <p className="text-sm text-gray-500 mb-6">
            如需存取此功能，請聯繫系統管理員提升權限。
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
            >
              返回上一頁
            </button>
            <a
              href="/"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              回首頁
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 頁面權限保護
 */
export const PageRoute = ({ page, children }) => (
  <ProtectedRoute page={page}>
    {children}
  </ProtectedRoute>
);

/**
 * 僅限管理員的路由 (visible_tags 包含 "*")
 */
export const AdminRoute = ({ children }) => {
  const { hasWildcardAccess } = usePermission();

  if (!hasWildcardAccess()) {
    return <AccessDenied />;
  }

  return (
    <ProtectedRoute>
      {children}
    </ProtectedRoute>
  );
};

/**
 * 任何已登入使用者可存取的路由
 */
export const AuthenticatedRoute = ({ children }) => (
  <ProtectedRoute>
    {children}
  </ProtectedRoute>
);

export default ProtectedRoute;
